// The pay viewer's pages (/people…), built for a parent route: the signed-in tree, and an
// admin's /view-as/$userId when the viewed user is a pay viewer (ADR 0028's A20
// amendment). As in payRoutes, the pages read their params and search without the
// parent's types and navigate by path, keeping any /view-as/ prefix (shell/viewAs.ts).
import { createRoute, useLocation, useParams, type AnyRoute } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { useMe } from '../api/queries/me';
import { useTeamRep, type RepRef } from '../api/queries/team';
import { useTenant } from '../api/queries/tenant';
import { InShellError, InShellNotFound, ISO_DATE, PageGate, UUID, useHome } from '../shell/gates';
import { NotFound, PageLayout, PageSkeleton } from '../shell/Page';
import { useGo, useViewAs, withoutViewAs } from '../shell/viewAs';
import { payDateFromPath, urlDateFromIso } from '../urlDates';
import { parsePayHistorySearch, type PayHistorySearch } from '../views/payHistory/PayHistoryView';
import { parsePendingPaySearch, type PendingPaySearch } from '../views/pendingPay/PendingPayView';
import {
  parsePeopleSearch,
  peopleHref,
  PeopleView,
  rememberPeopleSearch,
  type PeopleSearch,
} from '../views/people/PeopleView';
import {
  RepCheckPage,
  RepPayHistoryPage,
  RepPendingPage,
  RepSearchPage,
  type RepPayFrame,
} from '../views/repPay/RepPayPages';
import { parseLineItemSearch, type LineItemSearchParams } from '../views/search/LineItemSearchView';
import { noSearch, useRouteSearch, useSetSearch } from './payRoutes';

/**
 * The person's ID and the pay date ISO (MM-DD-YYYY in the path). The param is personId,
 * not userId: under /view-as/$userId that names the viewed pay viewer.
 */
function usePersonParams(): { personId: string; payDate: string } {
  const params: { personId?: string; payDate?: string } = useParams({ strict: false });
  return { personId: params.personId ?? '', payDate: payDateFromPath(params.payDate ?? '') };
}

/**
 * People's access gate. Under /view-as/ a viewed user who isn't a pay viewer has no such
 * pages: not found, rather than the signed-in user's forbidden.
 */
function PeopleGate({ children }: { children: ReactNode }) {
  const viewAs = useViewAs();
  const { data: me } = useMe();
  if (viewAs && me && !me.sections.includes('people')) return <InShellNotFound />;
  return <PageGate page="people">{children}</PageGate>;
}

/** People: search, role and page in the URL, as on Users. */
function PeoplePage() {
  const viewAs = useViewAs();
  const { pathname, searchStr } = useLocation();
  // The person pages' breadcrumb goes back to this search (peopleHref). Not the next
  // page's: the location moves on before this page unmounts.
  useEffect(() => {
    if (withoutViewAs(pathname) === '/people') rememberPeopleSearch(searchStr, viewAs);
  }, [pathname, searchStr, viewAs]);
  return (
    <PeopleGate>
      <PageLayout page="people">
        <PeopleView
          search={useRouteSearch(parsePeopleSearch)}
          onSearchChange={useSetSearch<PeopleSearch>()}
        />
      </PageLayout>
    </PeopleGate>
  );
}

/**
 * One person's pay (ADR 0028, A19): the rep header gives their name and whether they have
 * pending pay, and is 404 for anyone outside the viewable set. `need` is what the page
 * needs besides: pending pay of their own, or the tenant's search. Behind People's gate,
 * so the header is only asked for by a pay viewer.
 */
function PersonPage(props: {
  need?: 'pending' | 'search';
  children: (frame: RepPayFrame, userId: string, person: RepRef | undefined) => ReactNode;
}) {
  return (
    <PeopleGate>
      <Person {...props} />
    </PeopleGate>
  );
}

function Person({
  need,
  children,
}: {
  need?: 'pending' | 'search' | undefined;
  children: (frame: RepPayFrame, userId: string, person: RepRef | undefined) => ReactNode;
}) {
  const { personId: userId } = usePersonParams();
  const viewAs = useViewAs();
  const home = useHome();
  const { data: tenant } = useTenant();
  const valid = UUID.test(userId);
  const person = useTeamRep(userId, valid);
  const notFound =
    !valid ||
    (person.error instanceof ApiError && person.error.status === 404) ||
    (need === 'pending' && person.data?.hasPendingPay === false) ||
    (need === 'search' && !!tenant && !tenant.features.includes('line_item_search'));
  if (notFound) return <NotFound home={home} />;
  const frame: RepPayFrame = {
    page: 'people',
    root: { label: 'People', href: peopleHref(viewAs) },
    repHref: `/people/${userId}`,
    name: person.data?.name,
  };
  return children(frame, userId, person.data);
}

/** A person's Pay History, under People. */
function PersonPayHistoryPage() {
  const search = useRouteSearch(parsePayHistorySearch);
  const setSearch = useSetSearch<PayHistorySearch>();
  const go = useGo();
  return (
    <PersonPage>
      {(frame, userId, person) => (
        <RepPayHistoryPage
          frame={frame}
          repId={userId}
          search={search}
          onSearchChange={setSearch}
          onOpenCheck={(payDate) => go(`/people/${userId}/checks/${urlDateFromIso(payDate)}`)}
          onOpenSearch={() => go(`/people/${userId}/search`)}
          onOpenPending={person?.hasPendingPay ? () => go(`/people/${userId}/pending`) : undefined}
        />
      )}
    </PersonPage>
  );
}

/** One of a person's checks, under People. */
function PersonCheckPage() {
  const { payDate } = usePersonParams();
  if (!ISO_DATE.test(payDate)) return <InShellNotFound />;
  return (
    <PersonPage>
      {(frame, userId) => <RepCheckPage frame={frame} repId={userId} payDate={payDate} />}
    </PersonPage>
  );
}

/** A person's pending pay, under People: only for someone who has it. */
function PersonPendingPage() {
  const search = useRouteSearch(parsePendingPaySearch);
  const setSearch = useSetSearch<PendingPaySearch>();
  return (
    <PersonPage need="pending">
      {(frame, userId, person) =>
        // Not before the header says they have pending pay: an intern's would be 404.
        person ? (
          <RepPendingPage frame={frame} repId={userId} search={search} onSearchChange={setSearch} />
        ) : (
          <PageSkeleton />
        )
      }
    </PersonPage>
  );
}

/** A person's line items, under People, while the tenant has search. */
function PersonSearchPage() {
  const search = useRouteSearch(parseLineItemSearch);
  const setSearch = useSetSearch<LineItemSearchParams>();
  return (
    <PersonPage need="search">
      {(frame, userId) => (
        <RepSearchPage frame={frame} repId={userId} search={search} onSearchChange={setSearch} />
      )}
    </PersonPage>
  );
}

/** The pay viewer's pages, as children of `parent`. */
export function peopleRoutes<TParent extends AnyRoute>(parent: TParent) {
  const route = <TPath extends string, TSearch extends object>(
    path: TPath,
    validateSearch: (search: Record<string, unknown>) => TSearch,
    component: () => ReactNode,
  ) =>
    createRoute({
      getParentRoute: () => parent,
      path,
      validateSearch,
      errorComponent: InShellError,
      component,
    });
  return [
    route('/people', parsePeopleSearch, PeoplePage),
    route('/people/$personId', parsePayHistorySearch, PersonPayHistoryPage),
    route('/people/$personId/checks/$payDate', noSearch, PersonCheckPage),
    route('/people/$personId/pending', parsePendingPaySearch, PersonPendingPage),
    route('/people/$personId/search', parseLineItemSearch, PersonSearchPage),
  ];
}
